import { Zap, Shield, Crown, Key, RefreshCw, Copy, CheckCircle, Link2, Unlink } from 'lucide-react';
import { useState, useEffect } from 'react';

interface Plan {
  id: string;
  name: string;
  token_quota: number;
  price_usd: number;
  max_cores: number;
}

interface BillingStatus {
  plan_id: string;
  plan_name: string;
  tokens_used: number;
  tokens_quota: number;
  api_token_hash: string;   // API Token: kết nối Eric_MEC_ComputeUse.exe, Ericoder...
  share_token: string;       // Share Token Key: chia sẻ credit với người khác
  is_shared: boolean;
}

export function BillingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [sharedTokenInput, setSharedTokenInput] = useState('');
  const [savingToken, setSavingToken] = useState(false);
  const [copiedApi, setCopiedApi] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        fetch('/v1/plans'),
        fetch('/v1/billing/status', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
        })
      ]);
      if (statusRes.status === 401) {
        localStorage.removeItem('eric_token');
        localStorage.removeItem('eric_role');
        window.location.href = '/login';
        return;
      }
      const plansData = await plansRes.json();
      const statusData = await statusRes.json();
      if (Array.isArray(plansData)) setPlans(plansData);
      setStatus(statusData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const copyToClipboard = async (text: string, type: 'api' | 'share') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'api') {
        setCopiedApi(true);
        setTimeout(() => setCopiedApi(false), 2000);
      } else {
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 2000);
      }
    } catch {
      // Fallback for non-secure context
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      if (type === 'api') {
        setCopiedApi(true);
        setTimeout(() => setCopiedApi(false), 2000);
      } else {
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 2000);
      }
    }
  };

  const handleUpgrade = async (plan: Plan) => {
    if (status?.is_shared) {
      return alert("Bạn đang dùng chung token của tài khoản khác, không thể tự nâng cấp.\nHãy huỷ dùng chung trước (tính năng đang phát triển).");
    }
    setUpgrading(plan.id);
    try {
      const res = await fetch('/v1/billing/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
        },
        body: JSON.stringify({ new_plan_id: plan.id, new_token_quota: plan.token_quota, new_max_cores: plan.max_cores })
      });
      const data = await res.json();
      if (data.success) { setTimeout(() => { fetchData(); setUpgrading(null); }, 1000); }
      else { alert("Upgrade failed: " + data.error); setUpgrading(null); }
    } catch { alert("Network error!"); setUpgrading(null); }
  };

  const handleSaveSharedToken = async () => {
    if (!sharedTokenInput.trim()) return alert("Vui lòng nhập Share Token Key!");
    setSavingToken(true);
    try {
      const res = await fetch('/v1/billing/shared-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
        },
        body: JSON.stringify({ shared_token: sharedTokenInput.trim() })
      });
      const data = await res.json();
      if (data.success) {
        alert("✅ Đã kết nối dùng chung thành công! Bạn đang sử dụng quota của tài khoản khác.");
        setSharedTokenInput('');
        fetchData();
      } else { alert("Lỗi: " + data.error); }
    } catch { alert("Network error!"); }
    finally { setSavingToken(false); }
  };

  const handleRegenerateToken = async () => {
    if (!confirm("⚠️ Tạo mã mới sẽ vô hiệu hóa cả API Token và Share Token Key cũ.\n\nNhững người đang dùng chung Share Token Key của bạn sẽ KHÔNG bị ảnh hưởng (họ đã liên kết theo tài khoản).\n\nBạn có chắc chắn muốn tạo mã mới?")) return;
    try {
      const res = await fetch('/v1/billing/generate-token', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
      });
      const data = await res.json();
      if (data.success) { fetchData(); }
      else { alert("Lỗi tạo mã: " + data.error); }
    } catch { alert("Network error!"); }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', marginTop: '80px', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚡</div>
      Đang tải thông tin billing...
    </div>
  );

  const availableCredits = Math.max(0, (status?.tokens_quota || 0) - (status?.tokens_used || 0));
  const maskToken = (t?: string) => {
    if (!t) return '—';
    if (t.length <= 10) return t;
    return t.substring(0, 7) + '•••' + t.substring(t.length - 4);
  };

  const TokenBox = ({ label, token, copied, type, description }: {
    label: string; token?: string; copied: boolean; type: 'api' | 'share'; description: string
  }) => (
    <div style={{
      background: type === 'api'
        ? 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(0,0,0,0.2))'
        : 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(0,0,0,0.2))',
      border: `1px solid ${type === 'api' ? 'rgba(99,102,241,0.25)' : 'rgba(139,92,246,0.25)'}`,
      borderRadius: '12px',
      padding: '20px 24px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <Key size={14} color={type === 'api' ? '#6366f1' : 'var(--accent-amber)'} />
        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          {label}
        </span>
      </div>
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px', lineHeight: '1.5' }}>{description}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          flex: 1, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '8px', padding: '9px 14px', fontFamily: 'monospace', fontSize: '13px',
          color: token ? 'var(--text-primary)' : 'var(--text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {token ? maskToken(token) : 'Chưa có mã — Bấm "Tạo mã mới"'}
        </div>
        <button
          id={`copy-${type}-token`}
          onClick={() => copyToClipboard(token || '', type)}
          disabled={!token}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px',
            borderRadius: '8px', border: `1px solid ${copied ? '#10b981' : 'rgba(255,255,255,0.1)'}`,
            background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)',
            color: copied ? '#10b981' : 'var(--text-secondary)',
            cursor: token ? 'pointer' : 'not-allowed', fontSize: '13px', whiteSpace: 'nowrap',
            transition: 'all 0.2s', opacity: token ? 1 : 0.5
          }}
        >
          {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
          {copied ? 'Đã sao chép!' : 'Sao chép'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', maxWidth: '1000px', margin: '0 auto' }}>

      <div style={{ marginBottom: '4px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '6px', color: 'var(--text-primary)' }}>Billing & Usage</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
          Quản lý gói dịch vụ, theo dõi quota và các token kết nối.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
            AVAILABLE CREDITS <span>💰</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--accent-primary)' }}>
            {availableCredits.toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {status?.is_shared ? "Đang dùng quota từ tài khoản Paid User" : "Quota theo gói hiện tại của bạn"}
          </div>
        </div>

        <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
            CURRENT PLAN <span>✨</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {status?.plan_name || 'No Plan'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {status?.is_shared ? "Đang dùng chung tài khoản Paid User" : "Priority access & higher rate limits"}
          </div>
        </div>
      </div>

      {/* ── TOKEN SECTION ─────────────────────────────── */}
      <div className="glass-panel" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
              🔑 Quản lý Token
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Erix MEC sử dụng 2 loại token với mục đích khác nhau.
            </div>
          </div>
          <button
            id="regenerate-all-tokens"
            onClick={handleRegenerateToken}
            className="btn btn-glass"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 18px', fontSize: '13px' }}
          >
            <RefreshCw size={14} />
            Tạo mã mới
          </button>
        </div>

        {/* API Token */}
        <TokenBox
          label="API Token"
          token={status?.api_token_hash}
          copied={copiedApi}
          type="api"
          description="Dùng để kết nối các công cụ: Eric_MEC_ComputeUse.exe, Ericoder, Claude Code bridge và các nền tảng tích hợp khác."
        />

        {/* Share Token Key */}
        <TokenBox
          label="Share Token Key"
          token={status?.share_token}
          copied={copiedShare}
          type="share"
          description="Dùng để chia sẻ với người khác. Ai nhập mã này sẽ dùng chung quota & quyền lợi từ gói của bạn."
        />
      </div>

      {/* ── NHẬP SHARE TOKEN (nếu muốn dùng nhờ) ─────── */}
      {!status?.is_shared ? (
        <div className="glass-panel" style={{ padding: '24px', border: '1px solid rgba(139, 92, 246, 0.2)', background: 'linear-gradient(rgba(139, 92, 246, 0.03), rgba(0,0,0,0.15))' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Link2 size={16} color="var(--accent-amber)" />
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Dùng chung tài khoản Paid User
            </span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px', lineHeight: '1.6' }}>
            Nhập <strong>Share Token Key</strong> của một tài khoản Paid User để dùng chung quota tokens và quyền lợi mô hình từ gói của họ.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              id="share-token-input"
              type="text"
              placeholder="Nhập Share Token Key (shr_xxxxx)..."
              value={sharedTokenInput}
              onChange={e => setSharedTokenInput(e.target.value)}
              disabled={savingToken}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)',
                color: 'var(--text-primary)', fontSize: '14px', outline: 'none'
              }}
            />
            <button
              id="link-shared-token-btn"
              className="btn btn-primary"
              onClick={handleSaveSharedToken}
              disabled={savingToken}
              style={{ padding: '0 24px', whiteSpace: 'nowrap' }}
            >
              {savingToken ? 'Đang kết nối...' : '🔗 Kết nối'}
            </button>
          </div>
        </div>
      ) : (
        <div className="glass-panel" style={{ padding: '20px 24px', border: '1px solid rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CheckCircle size={18} color="#10b981" />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>Đang dùng chung tài khoản Paid User</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>Bạn đang hưởng quota & quyền lợi từ gói của tài khoản đã chia sẻ.</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn-glass" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 14px', opacity: 0.7 }}>
              <Unlink size={12} />
              Huỷ liên kết (sắp có)
            </button>
          </div>
        </div>
      )}

      {/* ── PLAN CARDS ────────────────────────────────── */}
      <div>
        <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px', color: 'var(--text-primary)' }}>Upgrade your plan</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {plans.map((p) => {
            const isCurrent = status?.plan_id === p.id && !status?.is_shared;
            const isPopular = p.price_usd > 0 && p.price_usd < 50;

            let Icon = Zap;
            let color = 'var(--text-secondary)';
            if (p.price_usd > 0) { Icon = Shield; color = 'var(--accent-primary)'; }
            if (p.price_usd >= 90) { Icon = Crown; color = 'var(--accent-amber)'; }

            return (
              <div key={p.id} className={`glass-panel ${isPopular ? 'glow-box' : ''}`} style={{
                padding: '28px', display: 'flex', flexDirection: 'column',
                borderColor: isPopular ? 'var(--accent-primary)' : 'var(--border)',
                transform: isPopular ? 'scale(1.02)' : 'none'
              }}>
                <div style={{ color, marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Icon size={28} />
                  {isPopular && <span style={{ background: 'var(--gradient-main)', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, color: '#fff' }}>POPULAR</span>}
                </div>
                <h3 style={{ fontSize: '22px', fontWeight: 600 }}>{p.name}</h3>
                <div style={{ fontSize: '34px', fontWeight: 700, margin: '14px 0' }}>
                  ${Number(p.price_usd)} <span style={{ fontSize: '15px', color: 'var(--text-muted)', fontWeight: 400 }}>/ month</span>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px 0', display: 'flex', flexDirection: 'column', gap: '10px', color: 'var(--text-primary)', fontSize: '14px' }}>
                  <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> {(p.token_quota / 1000000).toFixed(1)}M Tokens Virtual Context</li>
                  <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> {p.max_cores} Concurrent Agent Sessions</li>
                  <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> Permanent Memory TTL</li>
                  {p.price_usd > 0 && <li><span style={{ color: isPopular ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>✓</span> Server-side Auto-Indexing (GitHub)</li>}
                  {p.price_usd >= 90 && <li><span style={{ color: 'var(--accent-amber)' }}>✓</span> Share Token Key cho team</li>}
                </ul>

                <button
                  id={`upgrade-plan-${p.id}`}
                  className={`btn ${isCurrent ? 'btn-glass' : 'btn-primary'}`}
                  style={{ marginTop: 'auto', width: '100%', padding: '12px' }}
                  onClick={() => !isCurrent && handleUpgrade(p)}
                  disabled={isCurrent || upgrading === p.id}
                >
                  {isCurrent ? '✓ Current Plan' : upgrading === p.id ? 'Upgrading...' : `Upgrade to ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
